// SheetletEdit.js
import { useState } from 'react'
import {
    SparkleIcon,
    GridFourIcon,
    RowsIcon,
    TrashIcon,
    FolderOpenIcon,
    FloppyDiskIcon,
    ArrowSquareOutIcon,
    PaletteIcon,
    SlidersIcon,
} from '@phosphor-icons/react'
import { useDispatch, useSelector } from 'react-redux'
import { deleteSheetlet, updateSheetletGrid, updateSheetletHeaders } from '../../../store/actions'
import { loadFromSavedSheetlet } from './SheetletAPI'
import { useApi } from '../../../api/api'
import Sheetlet from '../../screens/Source/Sheetlet/Sheetlet'
import { useModal, MODALS } from '../../modals'
import {
    ScoopMenu,
    ScoopMenuItem,
    ScoopMenuDivider,
    ScoopButton,
    ScoopMenuItemWithSubmenu,
    ScoopDialog,
} from '../../ui'
import styles from './SheetletEdit.module.scss'

export default function SheetletEdit({
    setIsMenuOpen,
    isSheetletASkeleton,
    setIsSheetletASkeleton,
    server,
    title,
    id,
    worksheetID,
    worksheetRange,
    worksheetNum,
    worksheetName,
    worksheetURL,
    handleTitleChange,
    editSheetletOpen,
    setEditSheetletOpen,
    shouldHideGrid,
    shouldHideHeaders,
    inboxes,
    isCursorMode,
    isEditMode,
}) {
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedItemToLoad, setSelectedItemToLoad] = useState(null)
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

    // Get auth details from the state
    const userID = useSelector((state) => state.auth.userID)
    const token = useSelector((state) => state.auth.token)
    const workspaceID = useSelector((state) => state.auth.workspaceID)

    const { postData } = useApi() // Use the useApi hook here

    const dispatch = useDispatch()
    const editElementModal = useModal(MODALS.CANVAS.EDIT_ELEMENT)
    const deleteModal = useModal(MODALS.COMMON.DELETE)
    const loadSheetletModal = useModal(MODALS.SOURCE.LOAD_SHEETLET)

    const open = Boolean(anchorEl)

    const handleClick = (event) => {
        setIsMenuOpen(true)
        setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setIsMenuOpen(false)
        setAnchorEl(null)
    }

    // Defensive wrapper to prevent canvas event interference
    const createMenuItemHandler = (handler) => (event) => {
        event.stopPropagation() // Prevent canvas drag selection
        handler(event)
        setAnchorEl(null) // Close menu
    }

    const handleDelete = () => {
        console.log('Deleting Sheetlet: ' + id)
        dispatch(deleteSheetlet(id))
        setIsMenuOpen(false)
        setAnchorEl(null)
    }

    const handleLoadFromSavedSheetlet = (sheetlet) => {
        if (sheetlet) {
            handleTitleChange({ target: { value: sheetlet.worksheetName } })
            loadFromSavedSheetlet(
                sheetlet,
                setIsSheetletASkeleton,
                dispatch,
                id,
                selectedItemToLoad,
                workspaceID
            )
        }
    }

    const getInboxID = () => {
        if (!inboxes) return
        for (const inbox of inboxes) {
            if (inbox.calculatedWorksheetID === worksheetID) {
                console.log(inbox)
                return inbox.inboxID
            }
        }
        return null
    }

    const handleSave = () => {
        const inboxID = getInboxID()
        if (!inboxID) return
        server.sheetPostData(
            {
                action: 'flushWorksheetCache',
                sheetRange: {
                    inboxID: inboxID,
                },
            },
            () => {
                setIsMenuOpen(false)
                setAnchorEl(null)
            }
        )
    }

    const TablePropertiesSubmenu = () => {
        return (
            <>
                <ScoopMenuItem
                    leftSection={<GridFourIcon size={18} weight="regular" />}
                    primary={!shouldHideGrid ? 'Hide Grid' : 'Show Grid'}
                    onClick={() => {
                        dispatch(updateSheetletGrid(id, !shouldHideGrid))
                    }}
                />
                <ScoopMenuItem
                    leftSection={<RowsIcon size={18} weight="regular" />}
                    primary={!shouldHideHeaders ? 'Hide Headers' : 'Show Headers'}
                    onClick={() => {
                        dispatch(updateSheetletHeaders(id, !shouldHideHeaders))
                    }}
                />
                {!isSheetletASkeleton && (
                    <ScoopMenuItem
                        leftSection={<FloppyDiskIcon size={18} weight="regular" />}
                        primary="Save to Google Sheets"
                        onClick={handleSave}
                    />
                )}
            </>
        )
    }

    return (
        <>
            <div>
                {((isEditMode && !isCursorMode) ||
                    (isEditMode && isCursorMode && !isSheetletASkeleton)) && (
                    <ScoopButton
                        variant="text"
                        size="xxs"
                        onClick={handleClick}
                        aria-label="Open menu"
                        className={styles.editButton}
                    >
                        <SparkleIcon size={24} weight="fill" className={styles.sparkleIcon} />
                    </ScoopButton>
                )}

                <ScoopMenu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    {!isCursorMode ? (
                        <>
                            {!isSheetletASkeleton && (
                                <ScoopMenuItem
                                    leftSection={<ArrowSquareOutIcon size={18} weight="regular" />}
                                    primary="Open"
                                    onClick={createMenuItemHandler(() => {
                                        window.open(worksheetURL, '_blank')
                                    })}
                                />
                            )}
                            <ScoopMenuItem
                                leftSection={<PaletteIcon size={18} weight="regular" />}
                                primary="Style"
                                onClick={createMenuItemHandler(() => {
                                    editElementModal.show({ objectId: id })
                                })}
                            />
                            <ScoopMenuItem
                                leftSection={<FolderOpenIcon size={18} weight="regular" />}
                                primary="Load"
                                onClick={createMenuItemHandler(() => {
                                    loadSheetletModal.show({
                                        setSheetlet: handleLoadFromSavedSheetlet,
                                        workspaceID,
                                        server,
                                        setSelectedItemToLoad,
                                    })
                                })}
                            />
                            <ScoopMenuDivider />
                            <ScoopMenuItemWithSubmenu
                                leftSection={<SlidersIcon size={18} weight="regular" />}
                                primary="Table properties"
                                submenu={<TablePropertiesSubmenu />}
                                placement="right-start"
                                offset={8}
                            />
                            <ScoopMenuDivider />
                            <ScoopMenuItem
                                leftSection={<TrashIcon size={18} weight="regular" />}
                                primary="Delete"
                                onClick={createMenuItemHandler(() => {
                                    deleteModal.show({
                                        title,
                                        type: 'Sheetlet',
                                        onDelete: handleDelete,
                                    })
                                })}
                            />
                        </>
                    ) : (
                        !isSheetletASkeleton && (
                            <ScoopMenuItem
                                leftSection={<FloppyDiskIcon size={18} weight="regular" />}
                                primary="Save to Google Sheets"
                                onClick={createMenuItemHandler(handleSave)}
                            />
                        )
                    )}
                </ScoopMenu>
            </div>
            <ScoopDialog
                open={editSheetletOpen}
                onClose={() => setConfirmDialogOpen(true)}
                maxWidth={false}
                className={styles.sheetletModal}
            >
                <div className={styles.sheetletModalContent}>
                    <Sheetlet workspaceID={workspaceID} userID={userID} token={token} />
                </div>
            </ScoopDialog>
        </>
    )
}
